/**
 * ModeratorQAPanel — judge/BTC duyệt câu hỏi public Q&A từ khán giả livestream.
 *
 * Hiển thị queue pending + đã moderated. Cho phép approve / approve-with-edit
 * / reject. Realtime update qua SSE từ CourtroomPage parent (qua refetch khi
 * receive courtroom.qa_submitted).
 */
import { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Check,
  Close,
  Edit,
  ExpandMore,
  QuestionAnswer,
} from '@mui/icons-material'

import {
  useListModerationQueueQuery,
  useModeratePublicQuestionMutation,
  type PublicQuestion,
} from '../store/api/publicQaApi'

export interface ModeratorQAPanelProps {
  courtroomId: string
  livestreamEnabled: boolean
  defaultExpanded?: boolean
}

type FilterKind = 'pending' | 'approved' | 'rejected' | 'all'

export function ModeratorQAPanel({
  courtroomId,
  livestreamEnabled,
  defaultExpanded = false,
}: ModeratorQAPanelProps) {
  const [filter, setFilter] = useState<FilterKind>('pending')
  const {
    data: questions = [],
    isLoading,
    refetch,
  } = useListModerationQueueQuery(
    { courtroomId, status: filter === 'all' ? undefined : filter },
    {
      pollingInterval: livestreamEnabled ? 10_000 : 60_000,
      skip: !courtroomId,
    },
  )
  const [moderate, { isLoading: moderating }] = useModeratePublicQuestionMutation()

  const [editingQ, setEditingQ] = useState<PublicQuestion | null>(null)
  const [editedText, setEditedText] = useState('')

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 }
    questions.forEach((q) => {
      if (q.status in c) c[q.status as keyof typeof c] += 1
    })
    return c
  }, [questions])

  const handleAction = async (
    q: PublicQuestion,
    action: 'approve' | 'reject',
    edited?: string,
  ) => {
    try {
      await moderate({
        courtroomId,
        questionId: q.id,
        action,
        edited_question: edited,
      }).unwrap()
      refetch()
    } catch (e: any) {
      alert(`Moderate thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  const sortedQuestions = useMemo(
    () =>
      [...questions].sort((a, b) =>
        (a.created_at || '') > (b.created_at || '') ? -1 : 1,
      ),
    [questions],
  )

  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mt: 1, borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <QuestionAnswer fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            Q&A khán giả (Moderation)
          </Typography>
          {counts.pending > 0 && (
            <Chip
              size="small"
              label={`${counts.pending} chờ duyệt`}
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
          {counts.approved > 0 && (
            <Chip size="small" label={`${counts.approved} đã duyệt`} color="success" variant="outlined" />
          )}
          {!livestreamEnabled && (
            <Chip size="small" label="Livestream tắt" color="default" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              label="Trạng thái"
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterKind)}
            >
              <MenuItem value="pending">Chờ duyệt</MenuItem>
              <MenuItem value="approved">Đã duyệt</MenuItem>
              <MenuItem value="rejected">Đã từ chối</MenuItem>
              <MenuItem value="all">Tất cả</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => refetch()}>
            Làm mới
          </Button>
        </Stack>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isLoading && sortedQuestions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            Không có câu hỏi nào{filter === 'pending' ? ' chờ duyệt' : ''}.
          </Typography>
        )}

        <Stack spacing={1}>
          {sortedQuestions.map((q) => (
            <Box
              key={q.id}
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor:
                  q.status === 'pending'
                    ? 'warning.main'
                    : q.status === 'approved'
                      ? 'success.main'
                      : 'divider',
                bgcolor:
                  q.status === 'pending'
                    ? 'rgba(255,167,38,0.04)'
                    : 'transparent',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Chip
                  size="small"
                  label={q.guest_name || 'Guest'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
                <Chip
                  size="small"
                  label={q.status}
                  color={
                    q.status === 'approved'
                      ? 'success'
                      : q.status === 'rejected'
                        ? 'error'
                        : 'warning'
                  }
                  sx={{ height: 20, fontSize: 11 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {q.created_at ? new Date(q.created_at).toLocaleString('vi-VN') : ''}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1 }}
              >
                {q.question}
              </Typography>
              {q.status === 'pending' && (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Duyệt">
                    <span>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleAction(q, 'approve')}
                        disabled={moderating}
                      >
                        <Check fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sửa rồi duyệt">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setEditingQ(q)
                          setEditedText(q.question)
                        }}
                        disabled={moderating}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Từ chối">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleAction(q, 'reject')}
                        disabled={moderating}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </AccordionDetails>

      {/* Edit dialog */}
      <Dialog open={!!editingQ} onClose={() => setEditingQ(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Sửa câu hỏi rồi duyệt</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Gốc từ {editingQ?.guest_name || 'Guest'}. Có thể chỉnh chính tả/ngữ pháp trước khi duyệt
            công khai.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Nội dung câu hỏi"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingQ(null)}>Hủy</Button>
          <Button
            color="success"
            variant="contained"
            disabled={!editedText.trim() || moderating}
            onClick={async () => {
              if (!editingQ) return
              await handleAction(editingQ, 'approve', editedText.trim())
              setEditingQ(null)
            }}
          >
            {moderating ? <CircularProgress size={16} /> : 'Duyệt với nội dung này'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  )
}
